import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { http } from 'hiya-common/src/utils/axios'
import { useSelector } from 'react-redux'

// 游戏阶段枚举，仅做 UI 状态划分，具体时间轴由后续接入服务端数据时补充
export enum SinbadPhase {
  BETTING = 'BETTING', // 下注阶段
  DRAWING = 'DRAWING', // 开奖阶段
  SETTLEMENT = 'SETTLEMENT', // 结算阶段
}

// 首页接口原始数据结构
export interface SinbadHomePage {
  // 投注金币档位列表
  bet_prices: number[]
  // 宝箱列表
  box_list: Array<{
    box_id: number // 宝箱ID
    odds: number // 倍数
    bet_amounts: number // 投注金币
    pre_win: number // 预期赢得金币
    box_icon: string // 宝箱图标
  }>
  round_no: string // 当前轮次
  day_round: string // 当日轮次
  coin_num: number // 用户当前金币数
  day_win: number // 当日赢的金币
  // 轮次状态：0 下注阶段、1 开奖阶段、2 开奖展示阶段
  round_status: number
  // 当前轮开出的宝箱 ID 列表
  round_result: number[]
  // 神兽大奖类型：0 未开大奖，1 小神兽，2 大神兽
  hit_prize_type: number
  // 历史记录
  history_result: Array<{
    round_no: string
    round_result: number[]
    hit_prize_type: number
  }>
  // 公共席位
  seat_user_list: Array<{
    mid?: number
    name?: string
    avatar?: string
    total_bet_amounts?: number
    bet_list?: Array<{
      bet_price: number // 投注挡位
      box_id: number // 投注宝箱 ID
      bet_amounts: number // 投注金币
    }>
  }>
  // 非坐席用户下注信息
  other_user?: {
    mid?: number
    name?: string
    avatar?: string
    bet_list?: Array<{
      bet_price: number // 投注挡位
      box_id: number // 投注宝箱 ID
      bet_amounts: number // 投注金币
    }>
  }
  status_expire: number // 当前阶段倒计时
  min_bet_coin_num: number // 最小下注后用户剩余金币
}

// 当前轮次基础信息
export interface SinbadRoundInfo {
  roundId: string
  phase: SinbadPhase
  statusExpire: number // 当前阶段结束的服务器时间戳（秒或毫秒，后续接入时统一）
  todayWinSum: number
}

export const get_sinbad_homepage = createAsyncThunk(
  'get_sinbad_homepage',
  async (arg, store: any) => {
    const {deviceinfo} = store.getState().env
    const resp = await http.post(
      '/live/game/sinbads_legend_homepage',
      {
        ...deviceinfo,
      },
      {
        xcErrorToast: true,
      }
    )

    return resp.data.data
  }
)

// 页面整体状态结构
export interface SinbadState {
  base: {
    isLoading: boolean
    bet_amount: number
    playing: boolean
  }
  // 首页原始数据，用于还原/透传给各个区域
  homePage: Partial<SinbadHomePage>
}

export const initialSinbadState: SinbadState = {
  base: {
    isLoading: false,
    bet_amount: 0,
    playing: false,
  },
  homePage: {
    box_list: [],
    bet_prices: [],
    round_no: '',
    day_round: '',
    coin_num: 0,
    day_win: 0,
    round_status: 0,
    hit_prize_type: 0,
    history_result: [],
    seat_user_list: [],
    other_user: {},
    status_expire: 0,
    min_bet_coin_num: 0,
  },
}


export interface RootStateWithSinbad {
  GameSinbad?: SinbadState
}

export const gameSinbadSlice = createSlice({
  name: 'GameSinbad',
  initialState: initialSinbadState,
  reducers: {
    // 切换加载态，用于 loading 页与主内容切换
    changeBetLoadingAction: (state, action: PayloadAction<boolean>) => {
      Object.assign(state, {
        base: {
          ...state.base,
          isLoading: action.payload,
        },
      })
    },
    // 切换下注金额
    changeBetAmountAction: (state, action: PayloadAction<number>) => {
      Object.assign(state.base, {
        bet_amount: action.payload,
      })
    },
    // 背景音效开关
    updatePlayingAction: (state, action: PayloadAction<boolean>) => {
      Object.assign(state.base, {
        playing: action.payload,
      })
    },
    // WebMessage 317：轮次状态变更
    updateRoundInfoAction: (state, action: PayloadAction<{
      round_status?: number
      status_expire?: number
      round_result?: number[]
      hit_prize_type?: number
      round_no?: string
    }>) => {
      Object.assign(state.homePage, action.payload)
    },
    // WebMessage 318：座位席变更
    updateSeatUserListAction: (state, action: PayloadAction<SinbadHomePage['seat_user_list']>) => {
      Object.assign(state.homePage, {
        seat_user_list: action.payload || [],
      })
    },
    // WebMessage 318：非坐席用户下注信息
    updateOtherUserAction: (state, action: PayloadAction<SinbadHomePage['other_user']>) => {
      Object.assign(state.homePage, {
        other_user: action.payload || {},
      })
    },
    // 下注成功后更新余额和宝箱投注信息
    updateBetResultAction: (state, action: PayloadAction<{
      coin_num?: number
      box_list?: SinbadHomePage['box_list']
    }>) => {
      Object.assign(state.homePage, {
        ...action.payload,
      })
    },
  },
  extraReducers: (builder) => {
    builder.addCase(get_sinbad_homepage.fulfilled, (state, action) => {
      Object.assign(state.base, {
        bet_amount: action.payload?.bet_prices?.[0] || 0,
      })
      Object.assign(state.homePage, {
        ...action.payload,
      })
    })
  },
})

// 对外仍暴露旧的 action / reducer 名称，避免页面调用改动
export function changeBetLoading(payload: boolean) {
  return gameSinbadSlice.actions.changeBetLoadingAction(payload)
}

export const GameSinbadReducer = gameSinbadSlice.reducer
export const GameSinbadActions = gameSinbadSlice.actions

// 页面使用的 hook，统一从 GameSinbad 节点读取状态；如未挂载则回退到 initialSinbadState
export const useGameSinbadState = (): SinbadState => {
  return (
    useSelector((store: RootStateWithSinbad) => store.GameSinbad) ||
    initialSinbadState
  )
}


